// Фабрика мутаций сущности.
//
// В каждой фиче лежал один и тот же код: `useInvalidate<Entity>` с ключом
// `['<сущности>', workspaceId]` плюс три почти одинаковых хука create/update/delete.
// Восемь фич — восемь копий, и расхождения появлялись именно в инвалидации
// (кто-то забывал префикс и не обновлял списки с фильтрами).
//
// Здесь описывается только то, что у сущностей действительно разное: функции
// клиента и корень ключа. Всё остальное — общее.
import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';

import { ApiError } from '../api-client/index.js';
import { useCurrentScope } from '../scope/useCurrentScope.js';

/**
 * A raced save: the row changed between the form's read and its write, so the
 * caller has to reload instead of keeping an outdated copy on screen.
 */
function isStaleWriteConflict(error: unknown): boolean {
  if (!(error instanceof ApiError) || error.status !== 409) {
    return false;
  }
  const details = error.details;
  return (
    typeof details === 'object' &&
    details !== null &&
    'code' in details &&
    details.code === 'stale_write'
  );
}

export interface EntityMutationsConfig<TItem, TCreate, TUpdate> {
  /** Корень ключа запросов сущности: `['tasks', workspaceId, …]` → `'tasks'`. */
  key: string;
  /**
   * Другие корни, которые ломает мутация. Заметка попадает в панели сделки и
   * контакта, поэтому её изменение обязано освежить и их списки.
   */
  alsoInvalidate?: string[];
  create?: (input: TCreate) => Promise<TItem>;
  update?: (id: string, input: TUpdate) => Promise<TItem>;
  remove?: (id: string) => Promise<void>;
}

export interface EntityMutations<TItem, TCreate, TUpdate> {
  useCreate: () => UseMutationResult<TItem, Error, TCreate>;
  useUpdate: () => UseMutationResult<TItem, Error, { id: string; input: TUpdate }>;
  useDelete: () => UseMutationResult<void, Error, string>;
}

export function createEntityMutations<TItem, TCreate = unknown, TUpdate = unknown>(
  config: EntityMutationsConfig<TItem, TCreate, TUpdate>,
): EntityMutations<TItem, TCreate, TUpdate> {
  // История сущности меняется при любой её мутации, поэтому корень `activities`
  // инвалидируется всегда: иначе дровер истории при staleTime показывал бы
  // состояние до правки.
  const roots = [config.key, ...(config.alsoInvalidate ?? []), 'activities'];

  /** Инвалидация по префиксу — накрывает и списки с фильтрами, и открытую карточку. */
  function useInvalidate(): () => Promise<void> {
    const queryClient = useQueryClient();
    const scope = useCurrentScope();
    const workspaceId = scope?.workspaceId ?? 'anonymous';
    return async () => {
      await Promise.all(
        roots.map((root) => queryClient.invalidateQueries({ queryKey: [root, workspaceId] })),
      );
    };
  }

  function assertConfigured<T>(fn: T | undefined, what: string): T {
    if (fn === undefined) {
      throw new Error(`${config.key}: мутация «${what}» не настроена`);
    }
    return fn;
  }

  return {
    useCreate() {
      const invalidate = useInvalidate();
      return useMutation<TItem, Error, TCreate>({
        mutationFn: (input) => assertConfigured(config.create, 'create')(input),
        onSuccess: () => invalidate(),
      });
    },

    useUpdate() {
      const invalidate = useInvalidate();
      return useMutation<TItem, Error, { id: string; input: TUpdate }>({
        mutationFn: ({ id, input }) => assertConfigured(config.update, 'update')(id, input),
        onSuccess: () => invalidate(),
        // A rejected stale write still means the screen is behind: refresh it
        // and let the caller show the conflict.
        onError: (error) => {
          if (isStaleWriteConflict(error)) {
            void invalidate();
          }
        },
      });
    },

    useDelete() {
      const invalidate = useInvalidate();
      return useMutation<void, Error, string>({
        mutationFn: (id) => assertConfigured(config.remove, 'delete')(id),
        onSuccess: () => invalidate(),
      });
    },
  };
}
